"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { PUBLIC_NAV_ITEMS, PUBLIC_TOOL_ITEMS, AUTH_ITEMS, MEGA_MENUS } from "@/lib/navigation";
import type { NavItem } from "@/lib/navigation";
import {
  Menu,
  X,
  Search,
  ChevronDown,
  LayoutGrid,
  Users,
  GraduationCap,
  FolderOpen,
  ExternalLink,
  Bookmark,
  Clock,
  TrendingUp,
  Keyboard,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";

const MEGA_MENU_COLUMN_CONFIG: Record<string, { icon: React.ElementType; columns: { title: string; key: string }[] }> = {
  knowledge: {
    icon: LayoutGrid,
    columns: [
      { title: "Topics", key: "popularTopics" },
      { title: "Collections", key: "collections" },
      { title: "Guides", key: "guides" },
      { title: "Quick Links", key: "quickLinks" },
    ],
  },
  community: {
    icon: Users,
    columns: [
      { title: "Topics", key: "popularTopics" },
      { title: "What's New", key: "newContent" },
      { title: "Trending", key: "trendingTags" },
      { title: "Quick Links", key: "quickLinks" },
    ],
  },
  learning: {
    icon: GraduationCap,
    columns: [
      { title: "Topics", key: "popularTopics" },
      { title: "Learning Paths", key: "collections" },
      { title: "Guides", key: "guides" },
      { title: "Quick Links", key: "quickLinks" },
    ],
  },
  resources: {
    icon: FolderOpen,
    columns: [
      { title: "Resources", key: "popularTopics" },
      { title: "Collections", key: "collections" },
      { title: "Quick Links", key: "quickLinks" },
      { title: "Trending", key: "trendingTags" },
    ],
  },
};

function MegaMenuContent({ item }: { item: NavItem }) {
  const menuKey = item.megaMenu;
  const menu = MEGA_MENUS[menuKey!];
  const config = MEGA_MENU_COLUMN_CONFIG[menuKey!];
  if (!menu || !config) return null;

  return (
    <div className="w-full max-w-5xl mx-auto px-5">
      <div className="flex gap-8">
        <div className="w-56 shrink-0">
          <div className="flex items-center gap-2 mb-4">
            <config.icon className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Featured</span>
          </div>
          <div className="space-y-3">
            {menu.featuredContent?.map((content, i) => (
              <Link
                key={i}
                href={content.href}
                className="group block rounded-xl p-3 transition-colors hover:bg-neutral-100"
              >
                <div className="flex items-start gap-2.5">
                  <span className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">{content.title}</p>
                    </div>
                    {content.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{content.description}</p>
                    )}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-6">
          {config.columns.map((col) => {
            const items = menu[col.key as keyof typeof menu];
            if (!items || !Array.isArray(items) || items.length === 0) return null;
            return (
              <div key={col.key}>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{col.title}</h4>
                <ul className="space-y-1.5">
                  {items.map((sub: any, i: number) => (
                    <li key={i}>
                      <Link
                        href={sub.href}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:translate-x-0.5 transition-all py-1"
                      >
                        <span className="text-muted-foreground/60 truncate">{sub.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {menu.trendingTags && menu.trendingTags.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Trending</h4>
              <div className="flex flex-wrap gap-1.5">
                {menu.trendingTags.slice(0, 6).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-muted-foreground hover:bg-neutral-200 hover:text-foreground cursor-pointer transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const pathname = usePathname();

  const isActive = useCallback(
    (href: string) => {
      if (href === "/") return pathname === "/";
      return pathname === href || pathname.startsWith(href + "/");
    },
    [pathname],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveMegaMenu(null);
  }, [pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchFocused(true);
      }
      if (e.key === "Escape") {
        setSearchFocused(false);
        setActiveMegaMenu(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchFocused]);

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 w-full border-b transition-all duration-200",
          scrolled
            ? "border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-sm"
            : "border-transparent bg-background",
        )}
        role="banner"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex h-14 items-center justify-between px-5">
            <div className="flex items-center gap-6">
              <Link href="/" className="flex items-center gap-2.5 group" aria-label="HR Shakti Home">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-foreground text-background text-xs font-bold transition-transform group-hover:scale-105">
                  HR
                </span>
                <span className="font-heading font-semibold text-foreground">Shakti</span>
              </Link>
              <nav className="hidden xl:flex items-center gap-0.5" aria-label="Primary">
                {PUBLIC_NAV_ITEMS.map((item) => (
<div
                     key={item.href}
                     className="relative"
                     onMouseEnter={() => item.megaMenu && setActiveMegaMenu(item.megaMenu)}
                     onMouseLeave={() => setActiveMegaMenu(null)}
                     onFocus={() => item.megaMenu && setActiveMegaMenu(item.megaMenu)}
                     onBlur={() => setActiveMegaMenu(null)}
                   >
                    <Link
                      href={item.href}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                        isActive(item.href)
                          ? "text-foreground bg-neutral-100"
                          : "text-muted-foreground hover:text-foreground hover:bg-neutral-100",
                      )}
                      aria-current={isActive(item.href) ? "page" : undefined}
                      aria-haspopup={item.megaMenu ? "true" : undefined}
                      aria-expanded={item.megaMenu ? activeMegaMenu === item.megaMenu : undefined}
                    >
                      {item.label}
                      {item.megaMenu && (
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 transition-transform duration-200",
                            activeMegaMenu === item.megaMenu && "rotate-180",
                          )}
                        />
                      )}
                    </Link>
                  </div>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Search (Cmd+K)"
                onClick={() => setSearchFocused(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild className="focus-visible:ring-2 focus-visible:ring-primary">
                  <Link href="/auth/login">Sign In</Link>
                </Button>
                <Button size="sm" asChild className="focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1">
                  <Link href="/auth/register">Join HR Shakti</Link>
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 xl:hidden focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Toggle mobile menu"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((prev) => !prev)}
              >
                {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

{activeMegaMenu && (
          <div
            className="absolute left-0 right-0 top-full bg-background border-b border-border shadow-xl"
            onMouseEnter={() => setActiveMegaMenu(activeMegaMenu)}
            onMouseLeave={() => setActiveMegaMenu(null)}
            role="menu"
            aria-orientation="vertical"
            aria-label="Navigation menu"
          >
            {activeMegaMenu && <MegaMenuContent item={PUBLIC_NAV_ITEMS.find((i) => i.megaMenu === activeMegaMenu)!} />}
          </div>
        )}
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-background xl:hidden" role="dialog" aria-modal="true" aria-label="Mobile navigation">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 h-14 border-b border-border">
              <span className="font-heading font-semibold">Menu</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-4" aria-label="Mobile navigation">
              <div className="space-y-1">
                {PUBLIC_NAV_ITEMS.map((item) => (
                  <div key={item.href}>
                    {item.megaMenu ? (
                      <button
                        className="flex w-full items-center justify-between px-3 py-2.5 text-sm rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-neutral-100"
                        onClick={() => setActiveMegaMenu((prev) => (prev === item.megaMenu ? null : item.megaMenu!))}
                        aria-expanded={activeMegaMenu === item.megaMenu}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 transition-transform duration-200",
                            activeMegaMenu === item.megaMenu && "rotate-180",
                          )}
                        />
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        className={cn(
                          "block px-3 py-2.5 text-sm rounded-md transition-colors",
                          isActive(item.href)
                            ? "text-foreground bg-neutral-100 font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-neutral-100",
                        )}
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                    {item.megaMenu && activeMegaMenu === item.megaMenu && (
                      <div className="ml-4 mt-1 space-y-1">
                        <MegaMenuContent item={item} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border space-y-1">
                {PUBLIC_TOOL_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-neutral-100 rounded-md transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>
            <div className="p-5 border-t border-border space-y-3">
              <Button variant="outline" className="w-full" asChild>
                <Link href="/auth/login" onClick={() => setMobileOpen(false)}>Sign In</Link>
              </Button>
              <Button className="w-full" asChild>
                <Link href="/auth/register" onClick={() => setMobileOpen(false)}>Join HR Shakti</Link>
              </Button>
            </div>
          </div>
        </div>
      )}

      {searchFocused && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          onClick={() => setSearchFocused(false)}
        >
          <div
            className="w-full max-w-xl mx-4 bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <Command>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <Search className="h-5 w-5 text-muted-foreground shrink-0" />
                <CommandInput
                  placeholder="Search content, pages, and commands..."
                  className="border-none bg-transparent py-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoFocus
                />
                <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[0.625rem] font-mono text-muted-foreground shrink-0">
                  ESC
                </kbd>
              </div>
              <div className="px-4 pb-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick Links</p>
                <div className="space-y-0.5">
                  <Link href="/search" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-neutral-100 transition-colors" onClick={() => setSearchFocused(false)}>
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <span>Search all content</span>
                    <kbd className="ml-auto text-[0.625rem] text-muted-foreground font-mono">/</kbd>
                  </Link>
                  <Link href="/ai-search" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-neutral-100 transition-colors" onClick={() => setSearchFocused(false)}>
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span>AI Search</span>
                  </Link>
                  <Link href="/knowledge-hub" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-neutral-100 transition-colors" onClick={() => setSearchFocused(false)}>
                    <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                    <span>Browse Knowledge Hub</span>
                  </Link>
                </div>
                <div className="mt-3 pt-3 border-t border-border/60">
                  <p className="text-xs text-muted-foreground">Press <kbd className="font-mono text-muted-foreground bg-muted px-1 rounded">Cmd+K</kbd> to open command palette</p>
                </div>
              </div>
            </Command>
          </div>
        </div>
      )}
    </>
  );
}
